import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Typography,
  Chip,
  Stack
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';

// Common timezones
const TIMEZONES = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Phoenix',
  'America/Anchorage',
  'Pacific/Honolulu',
  'UTC'
];

// Available apparatus options
const APPARATUS_OPTIONS = [
  { value: 'VT', label: 'Vault' },
  { value: 'UB', label: 'Uneven Bars' },
  { value: 'BB', label: 'Balance Beam' },
  { value: 'FX', label: 'Floor Exercise' },
  { value: 'PH', label: 'Pommel Horse' },
  { value: 'SR', label: 'Still Rings' },
  { value: 'PB', label: 'Parallel Bars' },
  { value: 'HB', label: 'High Bar' }
];

const SCORING_RULES = [
  { value: 'jop', label: 'JO Program (JOP)' },
  { value: 'xcel', label: 'Xcel' },
  { value: 'elite', label: 'Elite' },
  { value: 'fig', label: 'FIG International' }
];

const CompetitionDefaultsForm = ({ defaults, onSave }) => {
  const [formData, setFormData] = useState({
    apparatus_order: ['VT', 'UB', 'BB', 'FX'],
    scoring_rules: 'jop',
    default_timezone: 'America/New_York',
    default_status: 'draft',
    ...defaults
  });
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setFormData(prev => ({ ...prev, ...defaults }));
  }, [defaults]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(formData);
      setHasChanges(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Stack spacing={3}>
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Default Apparatus Order
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
            Select apparatus in the order they should appear by default
          </Typography>
          <Autocomplete
            multiple
            options={APPARATUS_OPTIONS}
            getOptionLabel={(option) => {
              if (typeof option === 'string') {
                const found = APPARATUS_OPTIONS.find(a => a.value === option);
                return found ? found.label : option;
              }
              return option.label;
            }}
            value={formData.apparatus_order.map(v =>
              APPARATUS_OPTIONS.find(a => a.value === v) || { value: v, label: v }
            )}
            onChange={(e, newValue) => {
              handleChange('apparatus_order', newValue.map(v => v.value || v));
            }}
            isOptionEqualToValue={(option, value) =>
              (option.value || option) === (value.value || value)
            }
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  label={option.label || option}
                  {...getTagProps({ index })}
                  key={option.value || option}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                placeholder="Select apparatus..."
              />
            )}
          />
        </Box>

        <FormControl fullWidth>
          <InputLabel>Default Scoring Rules</InputLabel>
          <Select
            value={formData.scoring_rules}
            label="Default Scoring Rules"
            onChange={(e) => handleChange('scoring_rules', e.target.value)}
          >
            {SCORING_RULES.map(rule => (
              <MenuItem key={rule.value} value={rule.value}>
                {rule.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Autocomplete
          options={TIMEZONES}
          value={formData.default_timezone}
          onChange={(e, newValue) => handleChange('default_timezone', newValue)}
          freeSolo
          renderInput={(params) => (
            <TextField
              {...params}
              label="Default Timezone"
              helperText="Timezone for new competitions"
            />
          )}
        />

        <FormControl fullWidth>
          <InputLabel>Default Competition Status</InputLabel>
          <Select
            value={formData.default_status}
            label="Default Competition Status"
            onChange={(e) => handleChange('default_status', e.target.value)}
          >
            <MenuItem value="draft">Draft</MenuItem>
            <MenuItem value="upcoming">Upcoming</MenuItem>
            <MenuItem value="published">Published</MenuItem>
          </Select>
        </FormControl>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={!hasChanges || saving}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
      </Stack>
    </Box>
  );
};

export default CompetitionDefaultsForm;
